import { productApi } from "#/api/productApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import SelectableTable from "#/components/tables/SelectableTable";
import type { TableSortProps } from "#/components/tables/BaseTable";
import { EmptyState } from "#/components/ui/EmptyState";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import { exportToCSV } from "#/utils/CsvUtils";
import { logger } from "#/utils/logger";
import { Button, Skeleton, Table, Text, TextInput, Title } from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { IconCopyPlus, IconDownload, IconEye, IconPlus, IconSearch, IconTrash, IconUpload } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import LastMovedText from "#/components/ui/LastMovedText";
import { formatLastMovedForExport } from "#/utils/dateTimeUtils";
import { tableColumnsConfig, type ColumnKey } from "#/config/tableColumnsConfig";
import { useTableColumns, type ColumnRenderer } from "#/hooks/useTableColumns";
import ImportCSVModal from "./components/ImportCSVModal";
import BulkDeleteCategoriesModal from "./components/BulkDeleteCategoriesModal";
import type { Product } from "#/types/products";

/** SelectableTable prepends its checkbox column. */
const LEADING_COLUMNS = 1;

const ProductsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canWrite, canDelete } = usePermission();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");

  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);

  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);

  const [
    importCsvModalOpened,
    { open: openImportCsvModal, close: closeImportCsvModal },
  ] = useDisclosure(false);

  const [
    bulkDeleteModalOpened,
    { open: openBulkDeleteModal, close: closeBulkDeleteModal },
  ] = useDisclosure(false);

  const { data: { products = [], total = 0, per_page = 0 } = {}, isFetching } =
    useQuery({
      queryKey: ["products", page, limit, debouncedSearch, sortBy, sortDirection],
      queryFn: async () => {
        const response = await productApi.getAll({
          page: page,
          limit: limit,
          sortBy,
          order: sortDirection,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        });
        if (!response.data) {
          throw new Error("Failed to fetch categories");
        }
        return {
          products: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      },
      enabled: true,
      staleTime: 1000 * 60,
    });

  // One renderer per admin-configurable column (compile-time complete). The
  // column key is also the backend sort key, so every column is sortable.
  const renderers: Record<ColumnKey<"products">, ColumnRenderer<Product>> = {
    name: { cell: (item) => item.name ?? "--", cellClassName: "min-w-[200px]", sortKey: "name" },
    sku: { cell: (item) => item.sku ?? "--", cellClassName: "min-w-[120px]", sortKey: "sku" },
    variant: { cell: (item) => item.variant ?? "--", cellClassName: "min-w-[150px]", sortKey: "variant" },
    cost: { cell: (item) => item.cost ?? "--", cellClassName: "min-w-[120px]", sortKey: "cost" },
    retail_price: {
      cell: (item) => item.retail_price ?? "--",
      cellClassName: "min-w-[120px]",
      sortKey: "retail_price",
    },
    procurement_time: {
      cell: (item) =>
        item.procurement_time != null ? t("common.days", { count: item.procurement_time }) : "--",
      cellClassName: "min-w-[140px]",
      sortKey: "procurement_time",
    },
    filling_capacity: {
      cell: (item) => item.filling_capacity ?? "--",
      cellClassName: "min-w-[150px]",
      sortKey: "filling_capacity",
    },
    tare_weight: {
      cell: (item) => item.tare_weight ?? "--",
      cellClassName: "min-w-[120px]",
      sortKey: "tare_weight",
    },
    number_of_items: {
      cell: (item) => item.number_of_items ?? 0,
      cellClassName: "min-w-[120px]",
      sortKey: "number_of_items",
    },
    number_of_sites: {
      cell: (item) => item.number_of_sites ?? 0,
      cellClassName: "min-w-[120px]",
      sortKey: "number_of_sites",
    },
    last_moved: {
      cell: (item) => <LastMovedText date={item.last_moved} />,
      cellClassName: "min-w-[140px]",
      sortKey: "last_moved",
    },
  };

  const columns = useTableColumns("products", renderers, { leadingColumns: LEADING_COLUMNS });

  const handleSortChange = (e: TableSortProps) => {
    const key = columns.sortKeyAt(e.index);
    if (!key) return;
    setPage(1);
    setSortDirection(e.direction);
    setSortBy(key);
    clearSelection();
  };

  // Export is a data export: every column, whatever the admin shows on screen.
  const exportHeaders = tableColumnsConfig.products.columns.map((column) => column.key);
  const exportHeaderLabels = Object.fromEntries(
    tableColumnsConfig.products.columns.map((column) => [column.key, t(column.label)]),
  );

  const prepareExportData = (productsToExport: typeof products) => {
    return productsToExport.map((product) => ({
      name: product.name || "",
      sku: product.sku || "",
      variant: product.variant || "",
      cost: product.cost || "",
      retail_price: product.retail_price || "",
      procurement_time: product.procurement_time ?? "",
      filling_capacity: product.filling_capacity || "",
      tare_weight: product.tare_weight || "",
      number_of_items: product.number_of_items ?? 0,
      number_of_sites: product.number_of_sites ?? 0,
      last_moved: formatLastMovedForExport(product.last_moved, t),
    }));
  };

  const handleExportCurrentPage = () => {
    if (!products || products.length === 0) return;

    const exportData = prepareExportData(products);
    const filename = `categories-export-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, exportHeaders, filename, exportHeaderLabels, t("common.utcNote"));
  };

  const handleExportAll = async () => {
    try {
      const response = await productApi.getAll({
        page: 1,
        limit: total,
        sortBy,
        order: sortDirection,
      });

      const allProducts = response.data.data;
      const exportData = prepareExportData(allProducts);
      const filename = `categories-export-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, exportHeaders, filename, exportHeaderLabels, t("common.utcNote"));
    } catch (error) {
      logger.error("Failed to export all categories:", error);
    }
  };

  const handleImportSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: ["products"] });
  };

  const clearSelection = () => {
    setSelectedIndices([]);
    setSelectedProducts([]);
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-2 relative">
      <div className="w-full flex flex-col gap-3">
        <div className="w-full flex items-center justify-between">
          <Title order={4}>{t("products.title")}</Title>
          <div className="gap-2 flex items-center ml-auto">
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                onClick={() => {
                  void navigate("/inventory/categories/new");
                }}
                leftSection={<IconPlus size={18} />}
                color="black"
                disabled={!canWrite()}
              >
                {t("products.addCategory")}
              </Button>
            </PermissionTooltip>
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                leftSection={<IconUpload size={18} />}
                color="green.5"
                onClick={openImportCsvModal}
                disabled={!canWrite()}
              >
                {t("common.importCsv")}
              </Button>
            </PermissionTooltip>
            <Button
              leftSection={<IconDownload size={18} />}
              variant="outline"
              onClick={openExportModal}
              disabled={!products || products.length === 0}
            >
              {t("common.exportCsv")}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 justify-between">
          <TextInput
            className="flex-1 max-w-96"
            placeholder={t("products.searchPlaceholder")}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
              clearSelection();
            }}
          />
          <div className="flex items-center gap-2 ml-auto">
            <Pagination
              page={page}
              total={total}
              limit={per_page}
              onChange={(newPage) => {
                setPage(newPage);
                clearSelection();
              }}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
                clearSelection();
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {isFetching && (
          <div className="w-full grid grid-cols-4 gap-4">
            {Array.from({ length: 8 }, (_, i) => ({ id: `dummy-${i}` })).map(
              (item) => (
                <Skeleton key={item.id} height={150} />
              )
            )}
          </div>
        )}

        {!isFetching && products.length <= 0 && (
          <div className="w-full h-64">
            <EmptyState
              title={t("products.noDataTitle")}
              description={t("products.noDataDescription")}
              cta={
                <PermissionTooltip disabled={!canWrite()}>
                  <Button
                    onClick={() => navigate("/inventory/categories/new")}
                    disabled={!canWrite()}
                  >
                    {t("products.addCategory")}
                  </Button>
                </PermissionTooltip>
              }
            />
          </div>
        )}

        {!isFetching && products.length > 0 && (
          <SelectableTable
            headers={[...columns.headers, t("common.actions")]}
            loading={isFetching}
            selectedIndices={selectedIndices}
            onSelectionChange={(newIndices) => {
              const selected = newIndices.map((index) => products[index]);
              setSelectedProducts(selected);
              setSelectedIndices(newIndices);
            }}
            allowSorting={true}
            sortableColumns={columns.sortableColumns}
            onSortChange={handleSortChange}
            rows={products.map((item) => {
              return (
                <Table.Tr key={item.id}>
                  {columns.visible.map((column) => (
                    <Table.Td key={column.key} className={column.cellClassName}>
                      {column.cell(item)}
                    </Table.Td>
                  ))}
                  <Table.Td className="min-w-[120px]">
                    <div className="w-full flex items-center justify-center gap-2">
                      <PermissionTooltip disabled={!canWrite()}>
                        <ActionButton
                          icon={IconCopyPlus}
                          variant="outline"
                          color="purple"
                          disabled={!canWrite()}
                          onClick={() => {
                            void navigate(`/inventory/categories/new?duplicateFrom=${item.id}`);
                          }}
                        />
                      </PermissionTooltip>
                      <ActionButton
                        icon={IconEye}
                        variant="outline"
                        onClick={() => {
                          void navigate(`/inventory/categories/${item.id}`);
                        }}
                      />
                    </div>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          />
        )}
      </div>

      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={() => Promise.resolve(handleExportCurrentPage())}
        onExportAll={handleExportAll}
        currentPageCount={products.length}
        totalCount={total}
      />

      <ImportCSVModal
        opened={importCsvModalOpened}
        onClose={closeImportCsvModal}
        onSuccess={handleImportSuccess}
      />

      <BulkDeleteCategoriesModal
        opened={bulkDeleteModalOpened}
        onClose={closeBulkDeleteModal}
        selectedProducts={selectedProducts}
        onSuccess={() => {
          clearSelection();
          void queryClient.invalidateQueries({ queryKey: ["products"] });
        }}
      />

      {selectedProducts.length > 0 && (
        <div className="absolute bottom-0 left-0 inset-x-0 z-50 flex items-center justify-between w-full bg-black/10 border-t border-black/50 backdrop-blur-sm p-4">
          <div className="flex items-center gap-2">
            <Text size="md" fw={600} c="dark">
              {selectedProducts.length} {t("common.selected")}
            </Text>
          </div>
          <div className="flex items-center gap-3">
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                leftSection={<IconTrash size={16} />}
                color="red"
                variant="filled"
                size="md"
                onClick={openBulkDeleteModal}
                disabled={!canDelete()}
              >
                {t("common.delete")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
